import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';
import { allAdditionalArticles } from '@/mocks/articles-index';
import Navbar from './components/Navbar';
import FooterSection from './components/FooterSection';

const heroBg = 'https://readdy.ai/api/search-image?query=A%20sophisticated%20editorial%20product%20photography%20scene%20with%20warm%20neutral%20tones%20featuring%20carefully%20arranged%20fashion%20items%20like%20premium%20sneakers%20a%20leather%20watch%20and%20a%20designer%20handbag%20on%20a%20light%20oak%20surface%20with%20soft%20diffused%20window%20light%20creating%20gentle%20atmospheric%20shadows%20modern%20lifestyle%20aesthetic%20with%20generous%20negative%20space&width=1800&height=1200&seq=hero-bg-main-v2&orientation=landscape&nocache=true';

const aboutBg = 'https://readdy.ai/api/search-image?query=An%20abstract%20warm%20toned%20minimalist%20texture%20photograph%20resembling%20fine%20raw%20linen%20fabric%20with%20subtle%20sage%20green%20and%20cream%20undertones%20soft%20organic%20grain%20patterns%20no%20distinct%20objects%20just%20pure%20elegant%20texture%20with%20natural%20light%20variation%20calming%20serene%20aesthetic&width=1600&height=900&seq=about-bg-v2&orientation=landscape&nocache=true';

const ctaBg = 'https://readdy.ai/api/search-image?query=A%20warm%20inviting%20lifestyle%20scene%20of%20a%20modern%20home%20office%20setup%20featuring%20an%20open%20laptop%20on%20a%20walnut%20desk%20alongside%20a%20steaming%20coffee%20cup%20a%20leather%20bound%20notebook%20and%20stylish%20fashion%20accessories%20including%20sunglasses%20and%20a%20watch%20bathed%20in%20soft%20golden%20morning%20sunlight%20streaming%20through%20a%20large%20window&width=1800&height=1000&seq=cta-bg-v2&orientation=landscape&nocache=true';

export default function Home() {
  useEffect(() => {
    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'oopbuyspread.boats',
        url: 'https://oopbuyspread.boats/',
        logo: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/5133451b25e408e4a25e4df97e05add8.jpeg',
        description: 'Der vertrauenswürdige Oopbuy Spreadsheet Einkaufsleitfaden für Deutschland mit 14 Modekategorien – von Schuhen über Streetwear bis Accessoires.',
        sameAs: [
          'https://kaspreadsheet.com',
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'oopbuyspread.boats',
        url: 'https://oopbuyspread.boats/',
        description: 'Oopbuy Spreadsheet Deutschland – Der umfassende Einkaufsleitfaden mit 14 Kategorien, sicheren Shopping-Tipps und aktuellen Produktempfehlungen.',
        inLanguage: 'de-DE',
        potentialAction: {
          '@type': 'SearchAction',
          target: 'https://oopbuyspread.boats/search?q={search_term_string}',
          'query-input': 'required name=search_term_string',
        },
      },
    ];

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schemas);
    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* ===== HERO SECTION ===== */}
      <section className="relative w-full min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={heroBg}
            alt="Oopbuy Spreadsheet Deutschland – Einkaufsleitfaden Hintergrund"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/45"></div>
        </div>

        <div className="relative z-10 w-full max-w-[1200px] mx-auto px-6 md:px-10 py-32 md:py-40 text-center">
          <div className="animate-fade-in-up">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight tracking-tight max-w-4xl mx-auto">
              Oopbuy Spreadsheet Deutschland – Der umfassende Einkaufsleitfaden
            </h1>
          </div>

          <div className="animate-fade-in-up animation-delay-200">
            <p className="mt-6 md:mt-8 text-base md:text-lg text-white/80 leading-relaxed max-w-2xl mx-auto">
              Entdecken Sie die größte deutschsprachige Sammlung von Oopbuy Spreadsheet-Kategorien, Einkaufsratgebern, sicheren Shopping-Tipps und regelmäßig aktualisierten Produktempfehlungen.
            </p>
          </div>

          <div className="animate-fade-in-up animation-delay-400 mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="w-full sm:w-auto px-8 py-3.5 rounded-full bg-accent-500 text-white font-semibold text-sm hover:bg-accent-600 transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
            >
              oopbuy spreadsheet
              <i className="ri-arrow-right-up-line"></i>
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="w-full sm:w-auto px-8 py-3.5 rounded-full border-2 border-white/60 text-white font-semibold text-sm hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
            >
              Buy it now
            </a>
          </div>

          <div className="animate-fade-in-up animation-delay-600 mt-8 flex flex-wrap items-center justify-center gap-4 md:gap-6 text-white/60 text-xs md:text-sm">
            <span className="flex items-center gap-1.5">
              <i className="ri-check-line text-accent-400"></i> Täglich aktualisiert
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-check-line text-accent-400"></i> Sichere Einkaufsinformationen
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-check-line text-accent-400"></i> Detaillierte Vergleichstabellen
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-check-line text-accent-400"></i> Kostenlose Guides
            </span>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-white/50 text-2xl"></i>
        </div>
      </section>

      {/* ===== ABOUT SECTION ===== */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src={aboutBg} alt="" className="w-full h-full object-cover object-center opacity-30" />
        </div>
        <div className="relative z-10 max-w-[900px] mx-auto px-6 md:px-10 text-center">
          <p className="text-sm font-medium text-foreground-500 uppercase tracking-widest mb-4">Was ist Oopbuy Spreadsheet?</p>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground-950 leading-tight mb-8">
            Ihr Schlüssel zu smarter Mode in Deutschland
          </h2>
          <p className="text-base md:text-lg text-foreground-600 leading-relaxed max-w-3xl mx-auto">
            Das <strong>Oopbuy Spreadsheet</strong> ist eine sorgfältig kuratierte und regelmäßig aktualisierte Ressource, die deutschen Käufern einen strukturierten Zugang zu den besten Modeangeboten bietet. Mit <strong>14 durchdachten Kategorien</strong> – von Schuhen über Streetwear bis zu Accessoires – ermöglicht es einen effizienten und sicheren Einkaufsprozess. Anders als herkömmliche Shopping-Plattformen vereint das Oopbuy Spreadsheet <strong>Transparenz, Community-Wissen und direkte Produktlinks</strong> in einer übersichtlichen Tabellenstruktur und ist dabei vollständig kostenlos nutzbar.
          </p>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: 'ri-time-line', title: 'Zeitersparnis', desc: 'Alle Produkte auf einen Blick', img: categories[0].image },
              { icon: 'ri-shield-check-line', title: 'Sicherheit', desc: 'Verifizierte Verkäufer', img: categories[3].image },
              { icon: 'ri-refresh-line', title: 'Aktualität', desc: 'Tägliche Updates', img: categories[1].image },
              { icon: 'ri-group-line', title: 'Community', desc: 'Geprüfte Bewertungen', img: categories[9].image },
            ].map((item, i) => (
              <div key={i} className="bg-background-50/90 rounded-lg overflow-hidden">
                <div className="aspect-[3/2] overflow-hidden">
                  <img src={item.img} alt={item.title} className="w-full h-full object-cover object-center" />
                </div>
                <div className="p-4 text-center">
                  <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center">
                    <i className={`${item.icon} text-xl text-primary-500`}></i>
                  </div>
                  <h4 className="font-semibold text-foreground-900 text-sm mb-1">{item.title}</h4>
                  <p className="text-xs text-foreground-500">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CATEGORY GRID SECTION ===== */}
      <section className="py-20 md:py-28 bg-background-100/50">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="text-center mb-12">
            <p className="text-sm font-medium text-foreground-500 uppercase tracking-widest mb-4">Beliebte Kategorien</p>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground-950 leading-tight">
              Finden Sie, was Sie suchen
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.slice(0, 6).map((cat) => (
              <a
                key={cat.id}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="group relative rounded-lg overflow-hidden aspect-[4/3] cursor-pointer"
              >
                <img
                  src={cat.image}
                  alt={`${cat.nameDe} Kategorie – Oopbuy Spreadsheet`}
                  className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3 className="text-white font-heading font-semibold text-xl">{cat.nameDe}</h3>
                  <p className="text-white/70 text-sm mt-1">Jetzt entdecken <i className="ri-arrow-right-line ml-1 text-xs"></i></p>
                </div>
              </a>
            ))}
          </div>

          <div className="mt-8 text-center">
            <p className="text-sm text-foreground-500 mb-4">Alle 14 Kategorien durchstöbern:</p>
            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-4 py-2 rounded-full border border-background-300 text-sm text-foreground-600 hover:border-primary-400 hover:text-primary-600 hover:bg-primary-50 transition-all whitespace-nowrap cursor-pointer"
                >
                  {cat.nameDe}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== DARK CATEGORY SECTION ===== */}
      <section className="py-20 md:py-28 bg-foreground-950">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8 mb-14">
            <div>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-white leading-tight">
                14 Kategorien.<br />Unendliche Möglichkeiten.
              </h2>
            </div>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-6 py-3 rounded-full border-2 border-white/30 text-white font-semibold text-sm hover:border-accent-400 hover:text-accent-400 transition-all whitespace-nowrap cursor-pointer flex items-center gap-2"
            >
              Buy it now
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>

          {/* Horizontal scroll cards */}
          <div className="flex gap-5 overflow-x-auto pb-4 -mx-2 px-2 scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {categories.slice(3, 11).map((cat) => (
              <a
                key={cat.id}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="flex-shrink-0 w-64 bg-background-100 rounded-lg overflow-hidden group cursor-pointer"
              >
                <div className="aspect-square overflow-hidden">
                  <img
                    src={cat.image}
                    alt={`${cat.nameDe} – Oopbuy Spreadsheet`}
                    className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-4 bg-background-50">
                  <h3 className="font-semibold text-foreground-900 text-sm">{cat.nameDe}</h3>
                  <p className="text-xs text-foreground-500 mt-1">Zur Kategorie <i className="ri-arrow-right-line text-xs"></i></p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ===== ARTICLE PREVIEW SECTION ===== */}
      <section className="py-20 md:py-28">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
            <div className="lg:w-3/5">
              <p className="text-sm font-medium text-accent-500 uppercase tracking-wider mb-3">
                <i className="ri-circle-fill text-[6px] align-middle mr-2"></i>Neuer Artikel
              </p>
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 leading-snug mb-6">
                Der ultimative Oopbuy Spreadsheet Guide für Deutschland – Sneaker, Streetwear und Luxusmode sicher kaufen
              </h2>
              <p className="text-base text-foreground-600 leading-relaxed mb-6">
                Unser neuester Guide deckt alles ab, was Sie über das Oopbuy Spreadsheet wissen müssen: von der Navigation durch die 14 Kategorien über Sicherheitstipps beim internationalen Einkauf bis hin zu detaillierten Vergleichen der beliebtesten Modeartikel. Speziell für den deutschen Markt optimiert.
              </p>
              <div className="flex items-center gap-4 mb-8">
                <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-user-line text-accent-600"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground-900">Oopbuy Team</p>
                  <p className="text-xs text-foreground-500">12 Min. Lesezeit · 16. Juli 2026</p>
                </div>
              </div>
              <Link
                to="/artikel/oopbuy-spreadsheet-guide-deutschland"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                Vollständigen Guide lesen
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="lg:w-2/5">
              <div className="rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=An%20editorial%20flat%20lay%20scene%20with%20an%20open%20laptop%20displaying%20colorful%20spreadsheet%20data%20surrounded%20by%20fashion%20items%20like%20a%20pair%20of%20retro%20sneakers%20folded%20denim%20and%20a%20wristwatch%20on%20a%20warm%20wooden%20desk%20surface%20soft%20natural%20window%20light%20from%20the%20side%20creating%20gentle%20shadows&width=800&height=1000&seq=article-preview-v2&orientation=portrait&nocache=true"
                  alt="Oopbuy Spreadsheet Guide Artikel"
                  className="w-full h-auto object-cover object-center"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 9 SEO ARTICLE CARDS ===== */}
      <section className="py-20 md:py-28 bg-background-100/50">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="text-center mb-12">
            <p className="text-sm font-medium text-foreground-500 uppercase tracking-widest mb-4">Einkaufsratgeber</p>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground-950 leading-tight">
              Oopbuy Spreadsheet SEO Guides
            </h2>
            <p className="mt-3 text-base text-foreground-600 max-w-2xl mx-auto">
              Vertiefen Sie Ihr Wissen mit unseren detaillierten Ratgebern zu jeder Kategorie und jedem Aspekt des Oopbuy Spreadsheets.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {allAdditionalArticles.map((article) => (
              <Link
                key={article.slug}
                to={`/artikel/${article.slug}`}
                className="group bg-background-50 rounded-lg overflow-hidden cursor-pointer transition-all hover:-translate-y-1"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2.5 py-0.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                      {article.category}
                    </span>
                    <span className="text-xs text-foreground-400 flex items-center gap-1">
                      <i className="ri-time-line text-[10px]"></i>
                      {article.readTime}
                    </span>
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-900 leading-snug mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-xs text-foreground-500 line-clamp-2 mb-3">
                    {article.metaDescription}
                  </p>
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 group-hover:text-primary-700 transition-colors">
                    Weiterlesen
                    <i className="ri-arrow-right-line text-[10px] group-hover:translate-x-0.5 transition-transform"></i>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA SECTION ===== */}
      <section className="relative py-28 md:py-36 overflow-hidden">
        <div className="absolute inset-0">
          <img src={ctaBg} alt="" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/35 to-black/50"></div>
        </div>

        <div className="relative z-10 max-w-[800px] mx-auto px-6 md:px-10 text-center">
          <h2 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-white leading-tight mb-6 tracking-wide">
            JETZT OOPBUY SPREADSHEET ENTDECKEN
          </h2>
          <p className="text-base md:text-lg text-white/75 leading-relaxed mb-10 max-w-xl mx-auto">
            Entdecken Sie die größte deutschsprachige Sammlung von Einkaufsratgebern und Kategorien
          </p>

          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-accent-500 text-white font-bold text-sm hover:bg-accent-600 transition-all whitespace-nowrap cursor-pointer group"
          >
            <span className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center">
              <i className="ri-shopping-bag-line text-white text-sm"></i>
            </span>
            OOPBUY SPREADSHEET
            <i className="ri-arrow-right-up-line group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"></i>
          </a>
        </div>
      </section>

      <FooterSection />
    </div>
  );
}